import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MapPin, Clock, User, Mail, FileText } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ServiceTerms } from "../components/ServiceTerms";
import { SERVICE_TERMS } from "../data/serviceTerms";

const TIME_SLOTS = ["Au plus vite", "Sous 1h", "11:00", "13:00", "15:00", "17:00", "19:00", "21:00", "23:00", "01:00", "03:00", "Choisir au téléphone"];
const ZONES = ["Cannes", "Antibes", "Juan-les-Pins", "Cagnes-sur-Mer", "Nice", "Villefranche-sur-Mer", "Beaulieu-sur-Mer", "Saint-Jean-Cap-Ferrat", "Èze", "Cap-d'Ail", "Monaco"];

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [settings, setSettings] = useState({ delivery_fee: SERVICE_TERMS.deliveryFee, min_order: SERVICE_TERMS.minimum });

  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    delivery_address: "",
    delivery_zone: "Nice",
    delivery_time: "Au plus vite",
    notes: "",
    age_confirmed: false,
  });

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length === 0 && !submitting) {
      // allow until we redirect
    }
  }, [items, submitting]);

  const total = subtotal + (settings.delivery_fee || 15);
  const meetsMin = subtotal >= (settings.min_order || 30);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!meetsMin) {
      setError(`Commande minimum ${formatPrice(settings.min_order || 30)}`);
      return;
    }
    if (!form.age_confirmed) {
      setError("Veuillez confirmer votre majorité.");
      return;
    }
    if (items.length === 0) {
      setError("Votre panier est vide.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/orders", {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image_url: i.image_url,
        })),
      });

      // Google Ads conversion event (non-blocking, fails silently if gtag not loaded)
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-18374793949",
            value: res.data.total,
            currency: "EUR",
            transaction_id: res.data.order_number,
          });
          window.gtag("event", "purchase", {
            transaction_id: res.data.order_number,
            value: res.data.total,
            currency: "EUR",
            items: items.map((i) => ({
              item_id: i.product_id,
              item_name: i.name,
              price: i.price,
              quantity: i.quantity,
            })),
          });
        }
      } catch (_) {
        // ignore tracking errors — must never break the order flow
      }

      clear();
      navigate(`/order-confirmation/${res.data.order_number}`, { state: { order: res.data } });
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi de la commande");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="checkout-empty">
        <div className="text-center">
          <p className="eyebrow mb-3">Panier vide</p>
          <h1 className="font-display text-4xl text-white">Aucun produit à commander</h1>
          <button onClick={() => navigate("/")} className="btn-outline-gold mt-6">
            Retour au catalogue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen container-x px-5 sm:px-8 py-10 sm:py-16" data-testid="checkout-page">
      <p className="eyebrow mb-3">Commande rapide</p>
      <h1 className="font-display text-4xl sm:text-5xl text-white leading-none mb-2">
        Quelques détails et <span className="italic text-gold">on s'occupe de tout</span>
      </h1>
      <p className="text-gray-400 max-w-2xl mt-3">
        Aucun débit à cette étape. Notre équipe vous rappelle pour confirmer la commande et votre règlement : espèces, carte bancaire à la livraison ou lien de paiement sécurisé.
      </p>
      <ServiceTerms testId="checkout-service-terms" className="mt-4" />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10 mt-12">
        <form onSubmit={handleSubmit} className="space-y-8" data-testid="checkout-form">
          <div>
            <p className="eyebrow mb-5">Vos coordonnées</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-1">
                  <User size={12} /> Nom complet *
                </label>
                <input
                  required
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                  data-testid="checkout-name-input"
                  className="input-line"
                  placeholder="Jean Dupont"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-1">
                  <Phone size={12} /> Téléphone *
                </label>
                <input
                  required
                  name="customer_phone"
                  value={form.customer_phone}
                  onChange={handleChange}
                  data-testid="checkout-phone-input"
                  className="input-line"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-1">
                  <Mail size={12} /> Email (optionnel)
                </label>
                <input
                  type="email"
                  name="customer_email"
                  value={form.customer_email}
                  onChange={handleChange}
                  data-testid="checkout-email-input"
                  className="input-line"
                  placeholder="vous@example.com"
                />
              </div>
            </div>
          </div>

          <div>
            <p className="eyebrow mb-5">Livraison</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-1">
                  <MapPin size={12} /> Adresse de livraison *
                </label>
                <input
                  required
                  name="delivery_address"
                  value={form.delivery_address}
                  onChange={handleChange}
                  data-testid="checkout-address-input"
                  className="input-line"
                  placeholder="12 Promenade des Anglais, étage 3"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider mb-1 block">Zone *</label>
                <select
                  name="delivery_zone"
                  value={form.delivery_zone}
                  onChange={handleChange}
                  data-testid="checkout-zone-select"
                  className="input-line bg-[#0a0f1a]"
                >
                  {ZONES.map((z) => (
                    <option key={z} value={z}>{z}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-1">
                  <Clock size={12} /> Créneau souhaité *
                </label>
                <select
                  name="delivery_time"
                  value={form.delivery_time}
                  onChange={handleChange}
                  data-testid="checkout-time-select"
                  className="input-line bg-[#0a0f1a]"
                >
                  {TIME_SLOTS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-2 mb-1">
              <FileText size={12} /> Notes (digicode, étage, instructions)
            </label>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={3}
              data-testid="checkout-notes-input"
              className="input-line resize-none border-b"
              placeholder="Digicode 1234B, sonner à Dupont, 4ème étage gauche"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="checkout-age-label">
            <input
              type="checkbox"
              name="age_confirmed"
              checked={form.age_confirmed}
              onChange={handleChange}
              data-testid="checkout-age-checkbox"
              className="mt-1 accent-[#d4af37]"
            />
            <span className="text-sm text-gray-300">
              Je certifie avoir 18 ans ou plus et accepte les conditions générales. L'abus d'alcool est dangereux pour la santé.
            </span>
          </label>

          {error && (
            <div className="border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3 text-sm" data-testid="checkout-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="checkout-submit-btn"
            className="btn-gold w-full text-base py-4"
          >
            {submitting ? "Envoi en cours..." : "Envoyer ma commande"}
          </button>
        </form>

        {/* Summary */}
        <aside className="bg-night-2 border border-subtle p-6 sm:p-8 h-fit lg:sticky lg:top-24" data-testid="checkout-summary">
          <p className="eyebrow mb-5">Récapitulatif</p>
          <ul className="space-y-3 mb-6 max-h-72 overflow-y-auto pr-2">
            {items.map((it) => (
              <li key={it.product_id} className="flex justify-between text-sm gap-3">
                <span className="text-gray-300">
                  <span className="text-[#d4af37] mr-2">{it.quantity}×</span>
                  {it.name}
                </span>
                <span className="text-white whitespace-nowrap">{formatPrice(it.price * it.quantity)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-300">
              <span>Sous-total</span>
              <span data-testid="summary-subtotal">{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span>Livraison</span>
              <span data-testid="summary-delivery-fee">{formatPrice(settings.delivery_fee || 15)}</span>
            </div>
            <div className="flex justify-between font-display text-2xl text-white pt-3 border-t border-white/10 mt-3">
              <span>Total</span>
              <span className="text-gold" data-testid="summary-total">{formatPrice(total)}</span>
            </div>
          </div>
          {!meetsMin && (
            <p className="text-xs text-amber-400 mt-4" data-testid="checkout-minimum-warning">
              Commande minimum {formatPrice(settings.min_order || 30)} — il manque {formatPrice((settings.min_order || 30) - subtotal)}.
            </p>
          )}
          <p className="text-xs text-gray-400 mt-6 text-center leading-relaxed" data-testid="checkout-payment-notice">
            Espèces, carte bancaire ou lien de paiement après confirmation, y compris à Monaco.
          </p>
        </aside>
      </div>
    </div>
  );
};

export default Checkout;
